// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useMutation } from '@tanstack/react-query';
import { Choice, Party } from '@daml/types';
import { useLedgerApiClient } from '../../../contexts/LedgerApiContext';
import { useParties } from '../../../contexts/PartiesContext';
import { useUserState } from '../../../contexts/UserContext';
import { LedgerExerciseArgs } from '../../../utils/LedgerApiClientV2';

export const useLedgerExercise = <T extends object, C, R, K>(choice: Choice<T, C, R, K>) => {
  const ledgerApi = useLedgerApiClient();
  const { party } = useParties();
  const { loggedInUser } = useUserState();

  return useMutation({
    mutationKey: ['ledgerExercise'],
    mutationFn: async (args: LedgerExerciseArgs<T, C>) => {
      if (!loggedInUser) {
        throw new Error('Trying to use ledger exercise without loggedInUser context!');
      }

      let actAs: Party[] | undefined;
      let readAs: Party[] | undefined;

      if (args.submitter) {
        actAs = [args.submitter];
      } else {
        actAs = args.actAs;
        readAs = args.readAs;
      }

      if (actAs === undefined || actAs.length === 0) {
        actAs = [party];
      }

      return ledgerApi.exercise(
        choice,
        args.cid,
        args.argument,
        loggedInUser.userId,
        args.meta,
        actAs,
        readAs,
      );
    },
  });
};
